import { Component, EventEmitter, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInput, MatInputModule } from '@angular/material/input';
import { FilterChip } from '../../models/filter-chip';
import { debounceTime, filter, Subject, switchMap, tap } from 'rxjs';
import { UsersService } from '../../services/user.service';
import { User } from '../../models/user';
import { Group } from '../../models/group';
import { GroupsService } from '../../services/groups.service';

@Component({
  selector: 'app-bulk-group-chooser',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    MatInputModule
  ],
  templateUrl: './bulk-group-chooser.component.html',
  styleUrls: ['./bulk-group-chooser.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BulkGroupChooserComponent),
      multi: true
    }
  ]
})
export class BulkGroupChooserComponent implements ControlValueAccessor {
  @Input() label: string = 'Filter';
  @Input() hint: string = '';
  @Input() chips: string[] = []; // List of possible chip types
  @Output() groupsChange = new EventEmitter<Group[]>();
  @ViewChild(MatInput) inputElement!: MatInput;
  @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger;
  readonly debouncer: Subject<Group[]> = new Subject<Group[]>();
  _options = new Subject<Group[]>();
  options = this._options.asObservable();

  _filter = new Subject<string>();
  filter = this._filter.asObservable();

  filterControl = new FormControl('');
  @Input() selectedGroups: Group[] = [];
  activeChip: FilterChip | null = null; // Tracks chip being edited

  onChange: (value: Group[]) => void = () => { };
  onTouched: () => void = () => { };

  constructor(private groupService: GroupsService) {
    this.debouncer.pipe(debounceTime(100)).subscribe(v => this.groupsChange.emit(this.selectedGroups))
    this.filterControl.valueChanges.pipe(
      filter(c => c != null && typeof c === "string"),
      debounceTime(100),
      tap(v => console.log(v)),
      switchMap(c => this.groupService.filterGroups([{ chip: null, value: c?.trim() }], 5))
    ).subscribe(resp => this._options.next(resp.groups));
  }

  ngOnChanges() {
    this.debouncer.next(this.selectedGroups);// Initial emit, if there are any...
  }

  /**
   * Handles Enter key when inputting free text filters.
   */
  addFreeTextFilter(event: MatChipInputEvent): void {
    console.info(this.addFreeTextFilter.name);
    // if (!rawValue) {
    //   this.autocomplete.closePanel(); // Assume the user is trying to clear the autocomplete with a second "enter" press.
    //   return; // Ignore empty values
    // }
    const group = this.autocomplete.activeOption?.value;
    if (group && !this.selectedGroups.find(su => su.id == group.id)) {
      this.selectedGroups.push(group);
    }

    // Clear input field after adding a filter
    //this.filterControl.setValue('');
    event.chipInput!.clear();
    this.updateValue();
  }

  /**
   * Removes a selected filter chip.
   */
  removeGroup(group: Group): void {
    console.info(this.removeGroup.name);
    this.selectedGroups = this.selectedGroups.filter(f => f !== group);
    this.updateValue();
  }

  handleEnterKey(event: Event) {
    console.info(this.handleEnterKey.name);
    this.filterControl.setValue('');
    this.inputElement.value = '';
    // console.debug(this.handleEnterKey.name);
    // if (this.autocomplete.activeOption) {
    //   // Manually emit optionSelected behavior
    //   this.selectUser({ option: { value: this.autocomplete.activeOption.value } } as MatAutocompleteSelectedEvent);
    //   this.filterControl.setValue('');
    //   event.preventDefault(); // Prevents chip input from handling Enter
    // }
  }

  /**
   * Handles chip selection from autocomplete.
   * - Moves the input into chip-edit mode.
   */
  selectGroup(event: MatAutocompleteSelectedEvent): void {
    console.debug(this.selectGroup.name);
    // console.info(this.selectUser.name);
    const selectedUser = event.option.value;
    if (!selectedUser || this.selectedGroups.find(su => su.id == selectedUser.id))
      return;
    this.filterControl.setValue('');
    this.inputElement.value = '';
    this.selectedGroups.push(selectedUser);
    this.updateValue();
  }

  private updateValue() {
    this.onChange(this.selectedGroups);
    this.debouncer.next(this.selectedGroups);
  }

  // **ControlValueAccessor Methods**
  writeValue(value: Group[]): void {
    this.selectedGroups = value || [];
  }

  registerOnChange(fn: (value: Group[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.filterControl.disable();
    } else {
      this.filterControl.enable();
    }
  }
}
